import React, { useEffect, useState } from "react";
import { 
  FaUserGraduate, 
  FaBook, 
  FaMoneyBillWave, 
  FaChalkboardTeacher,
  FaPhone,
  FaMobileAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaCalendarAlt,
  FaLock,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAlt,
  FaEye,
  FaDownload,
  FaTimes,
  FaExclamationTriangle
} from "react-icons/fa";
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import SummaryApi from '../common/index';

const AdminInstructorDashboard = () => {
  const [counts, setCounts] = useState({
    myStudents: 0,
    myCourses: 0,
    revenueShare: 0,
    activeCourses: 0
  });

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    mobile_number: '',
    alternate_mobile_number: '',
    address: '',
    email: '',
    is_active: '',
    is_self_registered: '',
    created_date: '',
    password: '',
    instructor_image: '',
    documents: []
  });

  const [timeSlots, setTimeSlots] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const slotsPerPage = 5;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedId = queryParams.get("id");
  const instructorId = encodedId ? atob(encodedId) : null;

  // Initialize dark mode from localStorage admin-theme only
  useEffect(() => {
    const savedTheme = sessionStorage.getItem("admin-theme");
    const shouldBeDark = savedTheme === "dark";
    setDarkMode(shouldBeDark);
    
    // Apply theme to document for global CSS support
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Apply dark mode to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');

    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
  };

  const formatImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath instanceof File) return URL.createObjectURL(imagePath);
    
    const baseUrl = SummaryApi.Images.url.replace('/api', '');
    if (imagePath.includes(baseUrl)) return imagePath;
    if (imagePath.startsWith('uploads/')) return `${baseUrl}/${imagePath}`;
    return `${baseUrl}/${imagePath}`;
  };

  const getDocumentIcon = (fileName) => {
    if (!fileName) return <FaFileAlt className="text-gray-500 dark:text-gray-400" />;
    
    const extension = fileName.split('.').pop().toLowerCase();
    
    switch(extension) {
      case 'pdf':
        return <FaFilePdf className="text-red-500" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-blue-500" />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel className="text-green-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FaFileImage className="text-purple-500" />;
      default:
        return <FaFileAlt className="text-gray-500 dark:text-gray-400" />;
    }
  };

  const getDocumentName = (documentUrl) => {
    if (!documentUrl) return 'Document';
    return documentUrl.split('/').pop();
  };

  const handleViewDocument = (documentUrl) => {
    if (documentUrl) {
      const fullUrl = formatImageUrl(documentUrl);
      setSelectedDocument(fullUrl);
      setShowDocumentModal(true);
    }
  };



  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = sessionStorage.getItem("admin-theme");
      const shouldBeDark = savedTheme === "dark";
      setDarkMode(shouldBeDark);
      
      // Apply theme to document
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    window.addEventListener("themeChanged", handleThemeChange);
    
    // Listen for storage changes (other tabs)
    const handleStorageChange = (e) => {
      if (e.key === "admin-theme") {
        handleThemeChange();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom theme toggle events
    const handleCustomThemeChange = () => {
      handleThemeChange();
    };
    
    window.addEventListener("adminThemeToggled", handleCustomThemeChange);
    
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adminThemeToggled", handleCustomThemeChange);
    };
  }, []);














  const handleDownloadDocument = async (documentUrl, event) => {
    event.stopPropagation();
    
    if (documentUrl) {
      try {
        const fullUrl = formatImageUrl(documentUrl);
        const fileName = getDocumentName(documentUrl);
        
        const response = await fetch(fullUrl);
        const blob = await response.blob();
        
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        
      } catch (error) {
        console.error('Error downloading document:', error);
        const fullUrl = formatImageUrl(documentUrl);
        window.open(fullUrl, '_blank');
      }
    }
  };

  const handleViewImage = () => {
    if (profile.instructor_image) {
      setShowImageModal(true);
    }
  };

  // ==========================================
  // UPDATED FETCH LOGIC
  // ==========================================
  // useEffect(() => {
  //   const fetchAllData = async () => {
  //     if (!instructorId) {
  //       setError("No instructor ID provided");
  //       setLoading(false);
  //       return;
  //     }

  //     setLoading(true);
  //     setError(null);

  //     try {
  //       // Prepare both API calls to run in parallel
  //       const profilePromise = axios({
  //         method: SummaryApi.instructor_by_admin_courses.method,
  //         url: SummaryApi.instructor_by_admin_courses.url,
  //         data: { instructor_id: instructorId },
  //         timeout: 10000,
  //       });

  //       // NEW: Fetch Counts using specific API
  //       const countsPromise = axios({
  //         url: SummaryApi.InstructorDashboardCounts.url,
  //         method: SummaryApi.InstructorDashboardCounts.method,
  //         data: { instructor_id: instructorId },
  //         timeout: 10000,
  //       });

  //       // Execute both requests
  //       const [profileResponse, countsResponse] = await Promise.all([profilePromise, countsPromise]);

  //       // 1. Handle Profile Data Response
  //       if (profileResponse.data.status && profileResponse.data.data) {
  //         const instructorData = profileResponse.data.data;
          
  //         const documents = [
  //           instructorData.document1,
  //           instructorData.document2,
  //           instructorData.document3,
  //           instructorData.document4,
  //           instructorData.document5
  //         ].filter(doc => doc !== null);

  //         setProfile({
  //           first_name: instructorData.first_name || '',
  //           last_name: instructorData.last_name || '',
  //           mobile_number: instructorData.mobile_number || '',
  //           alternate_mobile_number: instructorData.alternate_mobile_number || '',
  //           address: instructorData.address || '',
  //           email: instructorData.email || '',
  //           is_active: instructorData.is_active ? 'Active' : 'Inactive',
  //           created_date: instructorData.created_date || '',
  //           password: instructorData.password || '',
  //           instructor_image: formatImageUrl(instructorData.instructor_image || ''),
  //           documents: documents,
  //           is_self_registered: instructorData.is_self_registered || 0,
  //           courses: instructorData.courses || []
  //         });

  //         setTimeSlots(instructorData.time_slots || []);
  //       } else {
  //         throw new Error(profileResponse.data.message || 'Failed to fetch instructor profile');
  //       }

  //       // 2. Handle Counts Data Response
  //       if (countsResponse.data.status && countsResponse.data.data) {
  //         const countsData = countsResponse.data.data;
  //         setCounts({
  //           myStudents: countsData.total_students || 0,
  //           myCourses: countsData.total_courses || 0,
  //           revenueShare: countsData.total_revenue || 0,
  //           activeCourses: countsData.total_completed_courses || 0
  //         });
  //       } else {
  //         // Fallback if counts API fails but profile succeeds
  //         console.warn("Counts API returned failure status");
  //         setCounts({ myStudents: 0, myCourses: 0, revenueShare: 0, activeCourses: 0 });
  //       }

  //     } catch (error) {
  //       console.error('Error fetching dashboard data:', error);
  //       let errorMessage = 'Error fetching dashboard data';
        
  //       if (error.code === 'ECONNABORTED') {
  //         errorMessage = 'Request timeout. Server is not responding.';
  //       } else if (error.response) {
  //         errorMessage = `Server error: ${error.response.status} - ${error.response.statusText}`;
  //       } else if (error.request) {
  //         errorMessage = 'No response from server. Please check if the server is running.';
  //       } else {
  //         errorMessage = error.message || 'Unknown error occurred';
  //       }
        
  //       setError(errorMessage);
  //       // Reset states on error
  //       setCounts({ myStudents: 0, myCourses: 0, revenueShare: 0, activeCourses: 0 });
  //       setProfile({
  //         first_name: '', last_name: '', mobile_number: '', alternate_mobile_number: '',
  //         address: '', email: '', is_active: '', created_date: '', password: '', 
  //         instructor_image: '', documents: []
  //       });
  //       setTimeSlots([]);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchAllData();
  // }, [instructorId]);
  // ============== REFETCH FUNCTION (REUSABLE) ==============
  const fetchAllData = async () => {
    if (!instructorId) {
      setError("No instructor ID provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profilePromise = axios({
        method: SummaryApi.instructor_by_admin_courses.method,
        url: SummaryApi.instructor_by_admin_courses.url,
        data: { instructor_id: instructorId },
        timeout: 10000,
      });

      const countsPromise = axios({
        url: SummaryApi.InstructorDashboardCounts.url,
        method: SummaryApi.InstructorDashboardCounts.method,
        data: { instructor_id: instructorId },
        timeout: 10000,
      });

      const [profileResponse, countsResponse] = await Promise.all([profilePromise, countsPromise]);

      if (profileResponse.data.status && profileResponse.data.data) {
        const instructorData = profileResponse.data.data;

        const documents = [
          instructorData.document1,
          instructorData.document2,
          instructorData.document3,
          instructorData.document4,
          instructorData.document5
        ].filter(doc => doc !== null);

        setProfile({
          first_name: instructorData.first_name || '',
          last_name: instructorData.last_name || '',
          mobile_number: instructorData.mobile_number || '',
          alternate_mobile_number: instructorData.alternate_mobile_number || '',
          address: instructorData.address || '',
          email: instructorData.email || '',
          is_active: instructorData.is_active ? 'Active' : 'Inactive',
          created_date: instructorData.created_date || '',
          password: instructorData.password || '',
          instructor_image: formatImageUrl(instructorData.instructor_image || ''),
          documents: documents,
          is_self_registered: instructorData.is_self_registered || 0,
          courses: instructorData.courses || []
        });

        setTimeSlots(instructorData.time_slots || []);
      }

      if (countsResponse.data.status && countsResponse.data.data) {
        const countsData = countsResponse.data.data;
        setCounts({
          myStudents: countsData.total_students || 0,
          myCourses: countsData.total_courses || 0,
          revenueShare: countsData.total_revenue || 0,
          activeCourses: countsData.total_completed_courses || 0
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load instructor data. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  // ============== INITIAL FETCH + REAL-TIME UPDATE LISTENER ==============
  useEffect(() => {
    fetchAllData(); // Initial load

    const handleInstructorUpdate = () => {
      console.log("Instructor updated → refetching dashboard...");
      fetchAllData();
    };

    // Listen for custom event from edit tab
    window.addEventListener('instructorUpdated', handleInstructorUpdate);

    // Fallback via sessionStorage (in case event is blocked)
    const handleStorage = (e) => {
      if (e.key === 'instructorUpdated') {
        handleInstructorUpdate();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('instructorUpdated', handleInstructorUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, [instructorId]);







  const handleEdit = () => {
    if (instructorId) {
      const encodedId = btoa(instructorId.toString());
      window.open(`/admin-panel/create-instructor?id=${encodedId}`, '_blank');
    }
  };

  const indexOfLastSlot = currentPage * slotsPerPage;
  const indexOfFirstSlot = indexOfLastSlot - slotsPerPage;
  const currentSlots = timeSlots.slice(indexOfFirstSlot, indexOfLastSlot);
  const totalPages = Math.ceil(timeSlots.length / slotsPerPage);

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const timeString = time.toString();
    const timeWithoutMicroseconds = timeString.split('.')[0];
    const parts = timeWithoutMicroseconds.split(':');
    if (parts.length < 2) return timeString;
    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const parseTimeSlots = (slot) => {
    const days = [
      { key: 'sunday', label: 'Sunday' },
      { key: 'monday', label: 'Monday' },
      { key: 'tuesday', label: 'Tuesday' },
      { key: 'wednesday', label: 'Wednesday' },
      { key: 'thursday', label: 'Thursday' },
      { key: 'friday', label: 'Friday' },
      { key: 'saturday', label: 'Saturday' }
    ];

    const timeSlotsList = days
      .map(day => {
        const timeRange = slot[day.key];
        if (timeRange && timeRange !== 'N/A' && timeRange.includes('-')) {
          const [start, end] = timeRange.split('-');
          const formattedStart = formatTime(start);
          const formattedEnd = formatTime(end);
          return `${day.label}: ${formattedStart} - ${formattedEnd}`;
        }
        return null;
      })
      .filter(Boolean);

    return (
      <div className="space-y-1">
        {timeSlotsList.map((timeSlot, index) => (
          <div key={index} className="text-gray-800 dark:text-gray-200">
            {timeSlot}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading instructor data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 max-w-md w-full text-center transition-colors duration-300">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Error Loading Data</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
            Please check if the backend server is running.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Document Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-auto transition-colors duration-300">
            <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Document Viewer</h3>
              <button 
                onClick={() => setShowDocumentModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-4 flex justify-center">
              {selectedDocument && (
                selectedDocument.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                  <img 
                    src={selectedDocument} 
                    alt="Preview" 
                    className="max-w-full max-h-[600px] object-contain rounded-lg shadow-md"
                  />
                ) : (
                  <iframe 
                    src={selectedDocument} 
                    className="w-full h-96" 
                    title="Document Viewer"
                    frameBorder="0"
                  />
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-screen overflow-auto transition-colors duration-300">
            <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Instructor Image Preview</h3>
              <button 
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-6 flex justify-center">
              {profile.instructor_image && (
                <img 
                  src={profile.instructor_image} 
                  alt="Instructor Preview" 
                  className="max-w-full max-h-96 object-contain rounded-lg shadow-md"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <h2 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-white transition-colors duration-300">
        Instructor Dashboard 
      </h2>

      {/* Widgets Row - Displaying Counts from API */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="rounded-xl shadow-lg p-6 flex items-center bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 dark:from-blue-800 dark:via-blue-700 dark:to-blue-600 text-white transition-colors duration-300">
          <div className="p-3 rounded-xl bg-white/20 mr-4 shadow-md">
            <FaUserGraduate className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-sm opacity-90">Total Students</h3>
            <p className="text-2xl font-bold">{counts.myStudents}</p>
          </div>
        </div>

        <div className="rounded-xl shadow-lg p-6 flex items-center bg-gradient-to-r from-teal-600 via-teal-500 to-teal-400 dark:from-teal-800 dark:via-teal-700 dark:to-teal-600 text-white transition-colors duration-300">
          <div className="p-3 rounded-xl bg-white/20 mr-4 shadow-md">
            <FaBook className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-sm opacity-90">Total Courses</h3>
            <p className="text-2xl font-bold">{counts.myCourses}</p>
          </div>
        </div>

        <div className="rounded-xl shadow-lg p-6 flex items-center bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-300 dark:from-yellow-700 dark:via-yellow-600 dark:to-yellow-500 text-white transition-colors duration-300">
          <div className="p-3 rounded-xl bg-white/20 mr-4 shadow-md">
            <FaMoneyBillWave className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-sm opacity-90">Revenue Share</h3>
            <p className="text-2xl font-bold">₹{counts.revenueShare}</p>
          </div>
        </div>

        <div className="rounded-xl shadow-lg p-6 flex items-center bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 dark:from-pink-700 dark:via-pink-600 dark:to-pink-500 text-white transition-colors duration-300">
          <div className="p-3 rounded-xl bg-white/20 mr-4 shadow-md">
            <FaChalkboardTeacher className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-sm opacity-90">Completed Courses</h3>
            <p className="text-2xl font-bold">{counts.activeCourses}</p>
          </div>
        </div>
      </div>

      {/* Profile + Time Slots Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              {profile.instructor_image ? (
                <div className="relative">
                  <img
                    src={profile.instructor_image}
                    alt="Instructor"
                    className="w-16 h-16 rounded-full mr-4 object-cover shadow-md cursor-pointer"
                    onClick={handleViewImage}
                  />
                  <div 
                    className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={handleViewImage}
                    title="Click to view larger image"
                  >
                    <FaEye className="text-white text-lg" />
                  </div>
                </div>
              ) : (
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-500 dark:from-blue-600 dark:to-blue-700 rounded-full mr-4 flex items-center justify-center text-white font-bold text-lg shadow-md">
                  {profile.first_name ? profile.first_name.charAt(0) : "I"}
                </div>
              )} 
              <div>
                <h3 className="text-[22px] font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                  <span>
                    {profile.first_name || 'N/A'} {profile.last_name || ''}
                  </span>
      {profile.is_self_registered === 1 ? (
                          <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                            Self
                          </span>
                        ) : (
                          <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                            Admin
                          </span>
                        )}
                </h3>
                <p className="text-sm mt-2 text-gray-600 dark:text-gray-400">
                  Status:{" "}
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      profile.is_active === "Active"
                        ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border border-green-400 dark:border-green-600"
                        : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border border-red-400 dark:border-red-600"
                    }`}
                  >
                    {profile.is_active || "N/A"}
                  </span>
                </p>
              </div>
            </div>
            <button 
              className="bg-[#1f4774] dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 text-white px-4 py-1 rounded-lg shadow-md hover:from-blue-600 hover:to-blue-500 dark:hover:from-blue-700 dark:hover:to-blue-600 transition-all duration-300"
              onClick={handleEdit}
            >
              Edit Profile
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 mb-4">
            <p className="flex items-center text-gray-700 dark:text-gray-300">
              <FaPhone className="mr-2 text-blue-500 dark:text-blue-400" /> 
              Mobile Number : {profile.mobile_number || 'N/A'}
            </p>

            <p className="flex items-center text-gray-700 dark:text-gray-300">
              <FaMobileAlt className="mr-2 text-blue-500 dark:text-blue-400" /> 
              Alternate Mobile : {profile.alternate_mobile_number || 'N/A'}
            </p>

            <p className="flex items-center text-gray-700 dark:text-gray-300">
              <FaMapMarkerAlt className="mr-2 text-blue-500 dark:text-blue-400" /> 
              Address : {profile.address || 'N/A'}
            </p>

            <p className="flex items-center text-gray-700 dark:text-gray-300">
              <FaEnvelope className="mr-2 text-blue-500 dark:text-blue-400" /> 
              Email : {profile.email || 'N/A'}
            </p>

            <p className="flex items-center text-gray-700 dark:text-gray-300">
              <FaCalendarAlt className="mr-2 text-blue-500 dark:text-blue-400" /> 
              Joined Date : {formatDateTime(profile.created_date)}
            </p>

            <p className="flex items-center text-gray-700 dark:text-gray-300">
              <FaLock className="mr-2 text-blue-500 dark:text-blue-400" /> 
              Password : {profile.password || 'N/A'}
            </p>

<p className="flex items-center text-gray-700 dark:text-gray-300">
  <FaBook className="mr-2 text-blue-500 dark:text-blue-400" /> 
  Courses :{" "}
  {profile.courses && profile.courses.length > 0 
    ? profile.courses.map(course => course.course_title).join(", ")
    : <span className="text-gray-500 dark:text-gray-400">N/A</span>
  }
</p>
          </div>

          {/* Documents Section */}
          <div className="mt-6 border-t dark:border-gray-700 pt-4">
            <h4 className="text-lg font-semibold mb-3 text-blue-700 dark:text-blue-400">Documents</h4>
            {profile.documents.length > 0 ? (
              <div className="grid grid-cols-1 gap-2">
                {profile.documents.map((doc, index) => {
                  const isImage = doc && doc.match(/\.(jpg|jpeg|png|gif)$/i);
                  return (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                      onClick={() => isImage ? handleViewDocument(doc) : null}
                    >
                      <div className="flex items-center">
                        <span className="mr-2">{getDocumentIcon(doc)}</span>
                        <span className="text-sm truncate max-w-xs text-gray-800 dark:text-gray-200">{getDocumentName(doc)}</span>
                      </div>
                      <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                        {isImage ? (
                          <button 
                            onClick={() => handleViewDocument(doc)}
                            className="p-1 text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
                            title="View Document"
                          >
                            <FaEye />
                          </button>
                        ) : null}
                        <button 
                          onClick={(e) => handleDownloadDocument(doc, e)}
                          className="p-1 text-green-500 hover:text-green-700 dark:hover:text-green-400"
                          title="Download Document"
                        >
                          <FaDownload />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">N/A</p>
            )}
          </div>
        </div>

        {/* Time Slots Card - ENHANCED TABLE */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300">
          <h3 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-400 text-center">Time Slots</h3>
          <div className="overflow-x-auto">
       <table className="min-w-full border-collapse text-sm ">
              <thead>
                <tr className="bg-[#1C3664] hover:bg-blue-700 dark:from-blue-800 dark:to-blue-700 text-white">
                  <th className="px-4 py-3 text-left font-semibold border border-blue-400 dark:border-blue-600">S.No</th>
                  <th className="px-6 py-3 text-left font-semibold border border-blue-400 dark:border-blue-600">Course</th>
                  <th className="px-6 py-3 text-left font-semibold border border-blue-400 dark:border-blue-600">Schedule</th>
                  <th className="px-5 py-3 text-left font-semibold border border-blue-400 dark:border-blue-600">Start Date</th>
                  <th className="px-5 py-3 text-left font-semibold border border-blue-400 dark:border-blue-600">End Date</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {currentSlots.length > 0 ? (
                  currentSlots.map((slot, index) => {
                    const serialNumber = (currentPage - 1) * slotsPerPage + index + 1;
                    const course = profile.courses.find(c => c.course_id === slot.course_id);
                    const courseTitle = course ? course.course_title : 'Unknown Course';
                    const schedule = parseTimeSlots(slot);
                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700">{serialNumber}</td>
                        <td className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 max-w-xs truncate" title={courseTitle}>
                          {courseTitle}
                        </td>
                        <td className="px-6 py-3 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 text-md font-medium whitespace-nowrap">
  {schedule}
</td>
                         <td className="px-6 py-3 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 text-md font-medium whitespace-nowrap">
                          {slot.start_date ? formatDateTime(slot.start_date).split(' ')[0] : 'N/A'}
                        </td>
                         <td className="px-6 py-3 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 text-md font-medium whitespace-nowrap">
                          {slot.end_date ? formatDateTime(slot.end_date).split(' ')[0] : 'N/A'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic border border-gray-200 dark:border-gray-700">
                      No time slots available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {timeSlots.length > slotsPerPage && (
            <div className="flex justify-between items-center mt-5 text-sm">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-1 rounded-lg font-medium transition ${
                  currentPage === 1
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700'
                }`}
              >
                Previous
              </button>

              <span className="text-gray-700 dark:text-gray-300 font-medium">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-4 py-1 rounded-lg font-medium transition ${
                  currentPage === totalPages
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInstructorDashboard;